import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getSeasonalLaunchBySlug } from '../../data/seasonalLaunches';
import {
  LAUNCH_PAGE_TYPE,
  commerceCtaLabel,
  commerceLinkType,
  formatLaunchPrice,
  getLaunchBrandAccent,
  getLaunchImage,
  getLaunchIndexGroups,
  getLaunchPath,
  getLaunchProduct,
  getLaunchQrPath,
  getPrimaryLaunchProducts,
  youtubeVideoId,
} from '../../domain/seasonalLaunch';
import {
  AFFILIATE_PAGE_SOURCE,
  trackAffiliateOutboundClick,
} from '../../utils/enhancedAnalytics';
import { usePageSeo } from '../../seo/usePageSeo';
import { usePageJsonLd } from '../../seo/usePageJsonLd';
import { buildSeasonalLaunchJsonLd } from '../../seo/seasonalLaunchSeo';
import ShareQrBlock from '../share/ShareQrBlock';
import NotFoundPage from '../notFound/NotFoundPage';
import './SeasonalLaunchPage.css';
import { GEAR_PRODUCTS, gearPath } from '../../domain/gearCatalog';

function trackLaunchClick(launch, product, placement, module, ctaText) {
  try {
    trackAffiliateOutboundClick({
      productName: product.name,
      brand: product.brand || launch.brand,
      launchSeason: launch.launchSeason,
      category: product.category,
      affiliateUrl: product.affiliateUrl,
      linkType: commerceLinkType(product.affiliateUrl),
      productScope: product.scope,
      placement,
      module,
      ctaText,
      pageSource: AFFILIATE_PAGE_SOURCE.SEASONAL_LAUNCH,
      pageType: LAUNCH_PAGE_TYPE,
      pagePath: getLaunchPath(launch),
    });
  } catch {
    // Analytics must never block outbound navigation.
  }
}

function AffiliateCta({ launch, product, placement, module, className = '' }) {
  if (!product?.affiliateUrl) return null;
  const label = commerceCtaLabel(product);
  const catalogProduct = GEAR_PRODUCTS.find(p => p.guideProductId === product.id && p.guidePath === getLaunchPath(launch));
  return (
    <>
    <a
      className={`ab-launch-cta ${className}`.trim()}
      href={product.affiliateUrl}
      target="_blank"
      rel="sponsored noopener noreferrer"
      data-testid={`cta-${product.id}-${placement}`}
      onClick={() => trackLaunchClick(launch, product, placement, module, label)}
    >
      {label}
    </a>
    {catalogProduct && <Link className="ab-launch-catalog-link" to={gearPath(catalogProduct)}>Compare all prices →</Link>}
    </>
  );
}

function LaunchImage({ image, className = '', fit, priority = false }) {
  const media = getLaunchImage({ image });
  if (!media) return null;
  return (
    <img
      className={`ab-launch-img ${className}`.trim()}
      src={media.src}
      alt={media.alt || ''}
      width={media.width}
      height={media.height}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'low'}
      decoding="async"
      style={{ objectFit: fit || media.fit || 'contain' }}
    />
  );
}

function LaunchPrice({ product }) {
  if (product.launchPrice == null) return null;
  return (
    <p className="ab-launch-price">
      <span className="ab-launch-price-label">{product.priceLabel || 'Launch price'}</span>
      <span>{formatLaunchPrice(product.launchPrice)}</span>
    </p>
  );
}

function SpecList({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="ab-launch-specs">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

function ProductSummary({
  launch,
  product,
  placement,
  module,
  headingLevel = 'h3',
  showImage = false,
}) {
  if (!product) return null;
  const Heading = headingLevel;
  return (
    <article className="ab-launch-product" id={product.id} data-testid={`product-${product.id}`}>
      {showImage ? (
        <div className="ab-launch-product-media">
          <LaunchImage image={product.image} />
        </div>
      ) : null}
      <Heading>{product.name}</Heading>
      <LaunchPrice product={product} />
      <SpecList items={product.specs || product.extraFeatures} />
      {product.description ? <p>{product.description}</p> : null}
      {product.editorialNote ? <p>{product.editorialNote}</p> : null}
      {product.compatibilityNote ? (
        <p className="ab-launch-note">{product.compatibilityNote}</p>
      ) : null}
      {product.sourceUrl ? (
        <p className="ab-launch-note">
          <a href={product.sourceUrl} target="_blank" rel="noopener noreferrer">
            {product.sourceLabel || 'Manufacturer specifications'}
          </a>
        </p>
      ) : null}
      <AffiliateCta
        launch={launch}
        product={product}
        placement={placement || product.defaultPlacement}
        module={module}
      />
    </article>
  );
}

function LaunchVideo({ videoId, title }) {
  if (!videoId) return null;
  return (
    <div className="ab-launch-video" data-testid="launch-video">
      <iframe
        src={`https://www.youtube.com/embed/${encodeURIComponent(videoId)}`}
        title={title || 'Launch video'}
        loading="lazy"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

function ShelterTable({ launch }) {
  const columns = launch.shelterSection.productIds
    .map((id) => getLaunchProduct(launch, id))
    .filter(Boolean);
  const valueFor = (product, rowId) => {
    if (rowId === 'launch-price') return formatLaunchPrice(product.launchPrice);
    const map = {
      capacity: product.comparison?.capacity,
      'fishable-area': product.comparison?.fishableArea,
      weight: product.comparison?.weight,
      'pack-length': product.comparison?.packLength,
      fabric: product.comparison?.fabric,
      doors: product.comparison?.doors,
      windows: product.comparison?.windows,
      'gear-nets': product.comparison?.gearNets,
      'best-fit': product.comparison?.bestFit,
    };
    return map[rowId] || '';
  };

  return (
    <div className="ab-launch-table" role="table" aria-label="New Eskimo shelter comparison">
      <div className="ab-launch-table-head" role="row">
        <span role="columnheader">Spec</span>
        {columns.map((product) => (
          <span key={product.id} role="columnheader">
            {product.name}
          </span>
        ))}
      </div>
      {launch.shelterSection.comparisonRows.map((row) => (
        <div key={row.id} className="ab-launch-table-row" role="row">
          <span className="ab-launch-table-label" role="rowheader">
            {row.label}
          </span>
          <div className="ab-launch-table-values">
            {columns.map((product) => (
              <div key={`${row.id}-${product.id}`} className="ab-launch-table-cell" role="cell">
                <span className="ab-launch-table-mobile-name">{product.name}</span>
                <span>{valueFor(product, row.id)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ShelterLineup({ launch }) {
  return (
    <div className="ab-launch-lineup" data-testid="shelter-lineup">
      {launch.shelterSection.productIds.map((id) => {
        const product = getLaunchProduct(launch, id);
        if (!product) return null;
        const isPick = id === launch.featuredDecision.pickProductId;
        return (
          <article
            key={id}
            className={`ab-launch-lineup-card${isPick ? ' is-pick' : ''}`}
          >
            <div className="ab-launch-lineup-media">
              <LaunchImage image={product.image} />
            </div>
            {isPick ? <p className="ab-launch-badge">{launch.featuredDecision.pickLabel}</p> : null}
            <h3>{product.name}</h3>
            <p className="ab-launch-lineup-price">{formatLaunchPrice(product.launchPrice)}</p>
            <p className="ab-launch-lineup-role">{product.lineupRole}</p>
            <p className="ab-launch-lineup-metric">{product.lineupMetric}</p>
            <AffiliateCta
              launch={launch}
              product={product}
              placement="shelter_lineup"
              module="shelters"
            />
          </article>
        );
      })}
    </div>
  );
}

function FeaturedDecision({ launch }) {
  const module = launch.featuredDecision;
  const pick = getLaunchProduct(launch, module.pickProductId);
  return (
    <section id="featured-decision" className="ab-launch-decision" data-testid="featured-decision">
      <p className="ab-launch-eyebrow">Shelter decision</p>
      <h2>{module.heading}</h2>
      <div className="ab-launch-versus">
        {module.productIds.map((id) => {
          const product = getLaunchProduct(launch, id);
          if (!product) return null;
          const isPick = id === module.pickProductId;
          return (
            <article
              key={id}
              className={`ab-launch-versus-card${isPick ? ' is-pick' : ''}`}
            >
              <div className="ab-launch-versus-media">
                <LaunchImage image={product.image} />
              </div>
              {isPick ? <p className="ab-launch-badge">{module.pickLabel}</p> : null}
              <h3>{product.name}</h3>
              <p className="ab-launch-lineup-price">{formatLaunchPrice(product.launchPrice)}</p>
              <AffiliateCta
                launch={launch}
                product={product}
                placement={module.placement}
                module="featured_decision"
              />
            </article>
          );
        })}
      </div>
      <div className="ab-launch-delta">
        <h3>{module.deltaHeading}</h3>
        <p>{module.deltaLead}</p>
        <ul className="ab-launch-delta-strip">
          {module.deltas.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
      {pick ? (
        <p className="ab-launch-pick">
          <span>{module.pickLabel}</span> {pick.name}
        </p>
      ) : null}
      {module.copy.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </section>
  );
}

function OutbreakCompare({ launch }) {
  const module = launch.outbreakComparison;
  return (
    <section id="shelter-comparison" className="ab-launch-section">
      <p className="ab-launch-eyebrow">Value vs premium</p>
      <h2>{module.heading}</h2>
      <p>{module.intro}</p>
      {module.groups.map((group) => {
        const left = getLaunchProduct(launch, group.leftId);
        const right = getLaunchProduct(launch, group.rightId);
        if (!left || !right) return null;
        return (
          <div key={group.heading} className="ab-launch-size-compare">
            <h3>{group.sizeLabel || group.heading}</h3>
            <div className="ab-launch-size-grid">
              {[left, right].map((product, index) => (
                <div key={product.id} className="ab-launch-size-side">
                  <div className="ab-launch-size-media">
                    <LaunchImage image={product.image} />
                  </div>
                  <h4>{product.name}</h4>
                  <p className="ab-launch-price-inline">
                    Launch price {formatLaunchPrice(product.launchPrice)}
                  </p>
                  {index === 1 && group.priceDelta ? (
                    <p className="ab-launch-premium-lead">{group.priceDelta} gets:</p>
                  ) : null}
                  {index === 1 && group.premiumGets?.length ? (
                    <ul className="ab-launch-premium-list">
                      {group.premiumGets.map((fact) => (
                        <li key={fact}>{fact}</li>
                      ))}
                    </ul>
                  ) : (
                    <ul>
                      {(product.outbreakFacts || []).map((fact) => (
                        <li key={fact}>{fact}</li>
                      ))}
                    </ul>
                  )}
                  <AffiliateCta
                    launch={launch}
                    product={product}
                    placement={module.placement}
                    module="outbreak_comparison"
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {module.decisionCopy.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </section>
  );
}

function LaunchIndex({ launch }) {
  const index = launch.launchIndex;
  const groups = getLaunchIndexGroups(launch);
  if (!index || !groups.length) return null;

  let imageSlot = 0;

  return (
    <section id="whats-new" className="ab-launch-section ab-launch-index" data-testid="launch-index">
      <p className="ab-launch-eyebrow">{index.eyebrow}</p>
      <h2>{index.heading}</h2>
      {index.intro ? <p>{index.intro}</p> : null}
      <div className="ab-launch-index-meta">
        {index.countLabel ? <p className="ab-launch-index-count">{index.countLabel}</p> : null}
        {groups.some((group) => group.countLabel) ? (
          <ul className="ab-launch-index-counts">
            {groups
              .filter((group) => group.countLabel)
              .map((group) => (
                <li key={group.id}>{group.countLabel}</li>
              ))}
          </ul>
        ) : null}
      </div>
      {groups.map((group) => (
        <div key={group.id} className="ab-launch-index-group">
          <h3>{group.label}</h3>
          <ol className="ab-launch-index-grid">
            {group.products.map((product) => {
              const eager = imageSlot < 4;
              imageSlot += 1;
              return (
                <li key={product.id}>
                  <article
                    className="ab-launch-index-card"
                    data-testid={`launch-index-${product.id}`}
                  >
                    <div className="ab-launch-index-media">
                      <LaunchImage image={product.image} priority={eager} />
                    </div>
                    <div className="ab-launch-index-copy">
                      <p className="ab-launch-index-cat">{group.label}</p>
                      {product.indexBadge ? (
                        <p className="ab-launch-badge">{product.indexBadge}</p>
                      ) : null}
                      <h4>{product.name}</h4>
                      {product.launchPrice != null ? (
                        <p className="ab-launch-index-price">
                          {product.priceLabel ? <small>{product.priceLabel} </small> : null}
                          {formatLaunchPrice(product.launchPrice)}
                        </p>
                      ) : null}
                      {product.indexTakeaway ? <p>{product.indexTakeaway}</p> : null}
                      <AffiliateCta
                        launch={launch}
                        product={product}
                        placement="launch_index"
                        module="launch_index"
                      />
                      {product.indexDetailHref && product.indexDetailLabel ? (
                        <a className="ab-launch-index-jump" href={product.indexDetailHref}>
                          {product.indexDetailLabel} ↓
                        </a>
                      ) : null}
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </section>
  );
}

function LaunchGuideTransition({ launch }) {
  const transition = launch.launchIndex?.transition;
  if (!transition) return null;
  return (
    <section className="ab-launch-section ab-launch-guide-lead" data-testid="launch-guide-lead">
      <p className="ab-launch-eyebrow">{transition.eyebrow}</p>
      <h2>{transition.heading}</h2>
      {transition.copy ? <p>{transition.copy}</p> : null}
    </section>
  );
}

function GuideSections({ launch }) {
  return launch.guideSections.map((section) => (
    <section id={section.id} key={section.id} className="ab-launch-section">
      <h2>{section.heading}</h2>
      {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      <div className="ab-launch-detail-grid">
        {section.productIds?.map((id) => (
          <ProductSummary key={id} launch={launch} product={getLaunchProduct(launch, id)}
            module={section.id} placement="product_detail" showImage />
        ))}
      </div>
    </section>
  ));
}

function SeasonalLaunchGuide({ launch }) {
  const path = getLaunchPath(launch);
  const videoId = youtubeVideoId(launch);
  const jsonLd = useMemo(() => buildSeasonalLaunchJsonLd(launch), [launch]);
  const primaryProducts = getPrimaryLaunchProducts(launch);
  const visual = launch.visual || {};
  const brandAccent = getLaunchBrandAccent(launch, '#CE181E');

  usePageSeo({
    title: launch.seoTitle,
    description: launch.metaDescription,
    path,
  });
  usePageJsonLd(jsonLd);

  return (
    <article
      className={`ab-launch${launch.guideSections ? ' ab-launch-data-guide' : ''}`}
      data-testid="seasonal-launch-page"
      style={{ '--brand-accent': brandAccent }}
    >
      <header className="ab-launch-hero">
        <div className="ab-launch-inner ab-launch-hero-grid">
          <div className="ab-launch-hero-copy">
            <nav className="ab-launch-crumbs" aria-label="Breadcrumb">
              <ol>
                {launch.breadcrumbs.map((crumb, index) => {
                  const last = index === launch.breadcrumbs.length - 1;
                  return (
                    <li key={crumb.label}>
                      {crumb.path && !last ? <Link to={crumb.path}>{crumb.label}</Link> : crumb.label}
                    </li>
                  );
                })}
              </ol>
            </nav>
            <p className="ab-launch-kicker">{visual.guideLabel}</p>
            {visual.logo ? (
              <img
                className="ab-launch-logo"
                src={visual.logo.src}
                alt={visual.logo.alt}
                width={visual.logo.width}
                height={visual.logo.height}
                data-testid="launch-logo"
              />
            ) : null}
            <p className="ab-launch-season">{visual.seasonLabel}</p>
            <h1>{visual.heroTitle || launch.pageTitle}</h1>
            {launch.heroCta ? (
              <p><a className="ab-launch-cta" href={launch.heroCta.href}>{launch.heroCta.label}</a></p>
            ) : null}
            {launch.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            <p className="ab-launch-trust">{launch.independenceDisclosure}</p>
            <p className="ab-launch-disclosure">{launch.affiliateDisclosure}</p>
            <p className="ab-launch-meta">
              <span>{visual.updatedLabel}</span>
              <span>{visual.productCountLabel}</span>
            </p>
          </div>
          <div className="ab-launch-hero-visual">
            <LaunchImage
              image={visual.heroImage}
              className="ab-launch-hero-photo"
              priority
            />
          </div>
        </div>
      </header>

      <div className="ab-launch-body">
        <div className="ab-launch-inner">
          <LaunchVideo videoId={videoId} title={launch.videoTitle} />

          <nav className="ab-launch-jump" aria-label="On this page">
            {launch.jumpNav.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>

          <LaunchIndex launch={launch} />
          <LaunchGuideTransition launch={launch} />

          {launch.guideSections ? <GuideSections launch={launch} /> : (<>
          <section id="shelters" className="ab-launch-section">
            <p className="ab-launch-eyebrow">2026-27 shelters</p>
            <h2>{launch.shelterSection.heading}</h2>
            <p>{launch.shelterSection.intro}</p>
            <ShelterLineup launch={launch} />
            <ShelterTable launch={launch} />
            <FeaturedDecision launch={launch} />
            {launch.shelterSection.productIds.map((id) => (
              <ProductSummary
                key={id}
                launch={launch}
                product={getLaunchProduct(launch, id)}
                placement="product_detail"
                module="shelters"
              />
            ))}
          </section>

          <OutbreakCompare launch={launch} />

          <section id="e-connect" className="ab-launch-section ab-launch-econnect">
            <p className="ab-launch-eyebrow">Shelter system</p>
            <h2>{getLaunchProduct(launch, 'e-connect-shelter-link')?.sectionHeading}</h2>
            <figure className="ab-launch-econnect-hero">
              <LaunchImage image={visual.eConnectImage} className="ab-launch-cover" />
              <figcaption>TURN TWO SHELTERS INTO ONE BASECAMP</figcaption>
            </figure>
            <div className="ab-launch-rooms" aria-label="E-Connect room layout">
              <span>FISHING ROOM</span>
              <span className="ab-launch-rooms-link">E-CONNECT</span>
              <span>SLEEPING ROOM</span>
            </div>
            <ProductSummary
              launch={launch}
              product={getLaunchProduct(launch, 'e-connect-shelter-link')}
              placement="product_detail"
              module="e-connect"
              headingLevel="h3"
            />
          </section>

          <section id="e-hauler" className="ab-launch-section">
            <p className="ab-launch-eyebrow">Sled system</p>
            <h2>{launch.eHauler.heading}</h2>
            <div className="ab-launch-sysgrid">
              {launch.eHauler.paths.map((pathItem) => (
                <div key={pathItem.id} className="ab-launch-path">
                  <p className="ab-launch-kicker">{pathItem.kicker}</p>
                  <h3>{pathItem.heading}</h3>
                  <div className="ab-launch-sysrow">
                    {(pathItem.visualProductIds || pathItem.productIds).map((id, index) => {
                      const product = getLaunchProduct(launch, id);
                      if (!product) return null;
                      return (
                        <React.Fragment key={`${pathItem.id}-${id}`}>
                          {index > 0 ? <span className="ab-launch-plus">+</span> : null}
                          <div className="ab-launch-syspart">
                            <div className="ab-launch-sysmedia">
                              <LaunchImage image={product.image} />
                            </div>
                            <p>{product.name}</p>
                            <AffiliateCta
                              launch={launch}
                              product={product}
                              placement="ehauler_system"
                              module="e-hauler"
                            />
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <p className="ab-launch-result">{pathItem.result}</p>
                  <p className="ab-launch-formula">{pathItem.formula}</p>
                  {pathItem.copy ? <p>{pathItem.copy}</p> : null}
                  {pathItem.productIds.map((id) => (
                    <ProductSummary
                      key={id}
                      launch={launch}
                      product={getLaunchProduct(launch, id)}
                      placement={pathItem.placement}
                      module="e-hauler"
                    />
                  ))}
                </div>
              ))}
            </div>
          </section>

          <section id="e-hub" className="ab-launch-section ab-launch-utility">
            <p className="ab-launch-eyebrow">Accessory system</p>
            <h2>{launch.eHub.heading}</h2>
            <div className="ab-launch-points">
              {(launch.eHub.systemPoints || []).map((point) => (
                <div key={point.label}>
                  <strong>{point.value}</strong>
                  <span>{point.label}</span>
                </div>
              ))}
            </div>
            <div className="ab-launch-ehub-visual">
              {['e-hub-triple-rod-holder', 'e-hub-triple-mount'].map((id) => {
                const product = getLaunchProduct(launch, id);
                return (
                  <div key={id} className="ab-launch-syspart">
                    <div className="ab-launch-sysmedia">
                      <LaunchImage image={product?.image} />
                    </div>
                    <p>{product?.name}</p>
                    <AffiliateCta
                      launch={launch}
                      product={product}
                      placement="ehub_visual"
                      module="e-hub"
                    />
                  </div>
                );
              })}
            </div>
            <ProductSummary
              launch={launch}
              product={getLaunchProduct(launch, 'e-hub-triple-rod-holder')}
              placement="ehub"
              module="e-hub"
            />
            <div className="ab-launch-cross-sell">
              <p>{launch.eHub.crossSell.copy}</p>
              <AffiliateCta
                launch={launch}
                product={getLaunchProduct(launch, launch.eHub.crossSell.productId)}
                placement={launch.eHub.crossSell.placement}
                module="e-hub"
              />
            </div>
            <ProductSummary
              launch={launch}
              product={getLaunchProduct(launch, 'e-hub-waste-bag-holder')}
              placement="ehub"
              module="e-hub"
              showImage
            />
          </section>

          <section className="ab-launch-section">
            <ProductSummary
              launch={launch}
              product={getLaunchProduct(launch, 'aluminum-xl-tripod-chair')}
              placement="product_detail"
              module="other-gear"
              headingLevel="h2"
              showImage
            />
          </section>

          <section id="apparel" className="ab-launch-section">
            <p className="ab-launch-eyebrow">2026-27 apparel</p>
            <h2>{launch.apparel.heading}</h2>
            <div id="flex" className="ab-launch-flex">
              <div className="ab-launch-flex-media">
                <LaunchImage image={visual.flexImage} className="ab-launch-cover" />
              </div>
              <div>
                <h3>{launch.apparel.flex.heading}</h3>
                <p className="ab-launch-flex-lockup">FLEX JACKET + BIBS</p>
                <ul className="ab-launch-highlights">
                  {(launch.apparel.flex.highlights || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p>{launch.apparel.flex.intro}</p>
                <SpecList items={launch.apparel.flex.sharedSpecs} />
                <div className="ab-launch-cta-row">
                  {launch.apparel.flex.productIds.map((id) => {
                    const product = getLaunchProduct(launch, id);
                    return (
                      <div key={id} className="ab-launch-cta-pair">
                        <h4>{product.name}</h4>
                        <LaunchPrice product={product} />
                        <SpecList items={product.extraFeatures} />
                        <AffiliateCta
                          launch={launch}
                          product={product}
                          placement={launch.apparel.flex.placement}
                          module="flex_suit"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="ab-launch-traverse">
              <div className="ab-launch-product-media">
                <LaunchImage image={getLaunchProduct(launch, 'traverse-wool-jacket')?.image} />
              </div>
              <div>
                <ul className="ab-launch-highlights">
                  {(launch.apparel.traverseHighlights || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <ProductSummary
                  launch={launch}
                  product={getLaunchProduct(launch, 'traverse-wool-jacket')}
                  placement="apparel"
                  module="apparel"
                />
              </div>
            </div>

            <div className="ab-launch-apparel-grid">
              {['outpost-wool-hoodie', 'flag-hoodie', 'abyss-hoodie', 'dipped-gloves'].map((id) => {
                const product = getLaunchProduct(launch, id);
                if (!product) return null;
                return (
                  <article key={id} className="ab-launch-apparel-tile" id={id} data-testid={`product-${id}`}>
                    <div className="ab-launch-product-media">
                      <LaunchImage image={product.image} />
                    </div>
                    <h3>{product.name}</h3>
                    <LaunchPrice product={product} />
                    {id === 'flag-hoodie' ? (
                      <p>{launch.apparel.hoodiePair.sharedDescription}</p>
                    ) : product.description ? (
                      <p>{product.description}</p>
                    ) : null}
                    <AffiliateCta
                      launch={launch}
                      product={product}
                      placement="apparel"
                      module="apparel"
                    />
                  </article>
                );
              })}
            </div>
          </section>

          <section id="all-new-gear" className="ab-launch-section">
            <h2>All New 2026-27 Gear</h2>
            <p>
              The {primaryProducts.length} products below are new for the {launch.launchSeason}{' '}
              Eskimo launch. Comparison and cross-sell products are listed separately above.
            </p>
            <ol className="ab-launch-catalog">
              {primaryProducts.map((product) => (
                <li key={product.id}>
                  <div>
                    <a href={`#${product.id}`}>{product.name}</a>
                    {product.launchPrice != null ? (
                      <span>Launch price {formatLaunchPrice(product.launchPrice)}</span>
                    ) : null}
                  </div>
                  <AffiliateCta
                    launch={launch}
                    product={product}
                    placement="product_detail"
                    module="all_new_gear"
                  />
                </li>
              ))}
            </ol>
          </section>

          </>)}
          <section id="faq" className="ab-launch-section">
            <h2>FAQ</h2>
            {launch.faqs.map((item) => (
              <div key={item.question} className="ab-launch-faq">
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </div>
            ))}
          </section>

          <section className="ab-launch-section ab-launch-snapshot">
            <h2>{launch.snapshot.heading}</h2>
            <p>{launch.snapshot.copy}</p>
            {launch.sources?.length ? (
              <ul>{launch.sources.map((source) => (
                <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.label}</a></li>
              ))}</ul>
            ) : null}
          </section>

          {launch.shareAsset?.enabled ? (
            <section id="share" className="ab-launch-section ab-launch-share">
              <ShareQrBlock
                qrUrl={launch.shareAsset.shortUrl}
                displayUrl={launch.shareAsset.displayUrl}
                headline={launch.shareAsset.headline}
                supportingText={launch.shareAsset.supportingText}
                filename={launch.shareAsset.filename}
                barColor={launch.shareAsset.barColor}
                bottomBarColor={launch.shareAsset.bottomBarColor}
                variant="launch"
                fullScreenPath={getLaunchQrPath(launch)}
                title={launch.pageTitle}
                analytics={{
                  pageType: LAUNCH_PAGE_TYPE,
                  brand: launch.brand,
                  launchSeason: launch.launchSeason,
                }}
              />
            </section>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function SeasonalLaunchPage() {
  const { launchSlug } = useParams();
  const launch = getSeasonalLaunchBySlug(launchSlug);
  if (!launch) return <NotFoundPage />;
  return <SeasonalLaunchGuide launch={launch} />;
}
